import { groupBy } from 'es-toolkit/compat';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { ProductState } from '../../checkout/resourceSelection/redux/types';
import { isServiceCareLBeta } from '../../common/betaService';
import { type BookingSuccessMessage, SUCCESSFULLY_BOOKED } from '../../common/postMessage.types';
import { sortingByName } from '../../common/utils/sortUtils';
import type { OverviewResource } from './redux/types';
import { ServiceBodyContentContainer } from './ServiceBodyContentContainer';
import { ServiceBodyFooterContainer } from './ServiceBodyFooterContainer';
import { hasTwoOrMoreLevels } from './services/serviceOverviewLevelService';
import { makeGetServiceResourcesForSku } from './services/serviceOverviewResourceStateService';
import {
    findDefaultSelectedTab,
    findSmallestNonFreeTrialLevel,
    getTabKey,
    getVisibleTabs,
} from './services/tabLevelService';
import { fetchResourcesForSkuThunk } from './thunks/resourceActivations.thunk';
import { cancelSubscriptionThunk } from './thunks/subscriptionCancel.thunk';
import type { ServiceOverviewItem } from './types';

interface OwnProps {
    service: ServiceOverviewItem;
}

interface StateProps {
    resources: OverviewResource[];
}

interface DispatchProps {
    fetchServiceResources: (sku: string, level?: string) => void;
    updateSubscription: (service: ServiceOverviewItem, resourceIds: string[], level: string | undefined) => void;
}

type Props = OwnProps & StateProps & DispatchProps;

function ServiceBody(props: Props) {
    const { resources, service, fetchServiceResources, updateSubscription } = props;
    const tabs = Object.values(
        groupBy(props.service.productRatePlans, ratePlan => ratePlan.variantName ?? ratePlan.level)
    );
    const [selectedTab, setSelectedTab] = useState(findSmallestNonFreeTrialLevel(tabs));
    const [hasUserSelectedTab, setHasUserSelectedTab] = useState(false);
    const [selectedResources, setSelectedResources] = useState<OverviewResource[]>([]);

    useEffect(() => {
        const smallestLevel = findSmallestNonFreeTrialLevel(tabs);
        setSelectedTab(smallestLevel);
        setHasUserSelectedTab(false);
        setSelectedResources([]);
    }, [service.productRatePlans, service.sku]);

    useEffect(() => {
        const defaultSelectedTab = findDefaultSelectedTab(tabs, resources);
        const visibleTabs = getVisibleTabs(tabs, resources);
        const selectedTabIsVisible = visibleTabs.some(ratePlans => getTabKey(ratePlans) === selectedTab);
        const shouldAutoSelectDefaultTab = !hasUserSelectedTab && selectedTab !== defaultSelectedTab;
        const shouldRecoverFromHiddenTab = visibleTabs.length > 0 && !selectedTabIsVisible;

        if (shouldAutoSelectDefaultTab || shouldRecoverFromHiddenTab) {
            setSelectedTab(defaultSelectedTab);
            setSelectedResources(currentSelectedResources =>
                currentSelectedResources.length === 0 ? currentSelectedResources : []
            );
        }
    }, [hasUserSelectedTab, resources, selectedTab, service.productRatePlans]);

    useEffect(() => {
        fetchServiceResources(service.sku);
        window.addEventListener('message', listenToSuccessfulFinishOfBooking);
        return () => window.removeEventListener('message', listenToSuccessfulFinishOfBooking);
        // adding listen... function results in triggering for each render
    }, [service.sku, fetchServiceResources]);

    function listenToSuccessfulFinishOfBooking(message: MessageEvent) {
        if (message.origin !== window.origin) {
            return;
        }
        const messageData = message.data as unknown as BookingSuccessMessage;
        if (messageData.type === SUCCESSFULLY_BOOKED && messageData.productId === service.sku) {
            fetchServiceResources(service.sku);
            setSelectedResources([]);
            setTimeout(() => fetchServiceResources(service.sku), 5000);
        }
    }

    function handleSelectResource(resource: OverviewResource) {
        const selectedResourceIds = selectedResources.map(it => it.id);
        if (selectedResourceIds.includes(resource.id)) {
            setSelectedResources(selectedResources.filter(it => it.id !== resource.id));
        } else {
            setSelectedResources([...selectedResources, resource].sort(sortingByName));
        }
    }

    function handleSelectAll(resources: OverviewResource[], shouldBeSelected: boolean) {
        if (shouldBeSelected) {
            setSelectedResources(
                resources.filter(
                    resource =>
                        resource.productState === ProductState.ACTIVE &&
                        !isServiceCareLBeta(service.sku, resource.targetLevel)
                )
            );
        } else {
            setSelectedResources([]);
        }
    }

    function handleConfirmUpdate() {
        const ids = selectedResources.map(resource => resource.id);
        updateSubscription(service, ids, hasTwoOrMoreLevels(service) ? selectedTab : undefined);
        setSelectedResources([]);
    }

    function selectTab(tabKey: string) {
        if (tabKey !== selectedTab) {
            setHasUserSelectedTab(true);
            setSelectedTab(tabKey);
            setSelectedResources([]);
        }
    }

    return (
        <div className={'ServiceOverviewItemBodyResource'}>
            <ServiceBodyContentContainer
                service={service}
                selectedResources={selectedResources}
                fetchContent={() => fetchServiceResources(service.sku)}
                handleSelectAll={handleSelectAll}
                handleSelectResource={handleSelectResource}
                tabs={tabs}
                tab={selectedTab}
                selectTab={selectTab}
            />
            <hr className='margin-0' />
            <ServiceBodyFooterContainer
                service={service}
                tab={selectedTab}
                selectedResources={selectedResources}
                handleConfirmUpdate={handleConfirmUpdate}
            />
        </div>
    );
}

export function mapDispatchToProps(dispatch: RootDispatch) {
    return {
        fetchServiceResources: (sku: string) => dispatch(fetchResourcesForSkuThunk(sku)),
        updateSubscription: (subscription: ServiceOverviewItem, resourceIds: string[], level: string | undefined) =>
            dispatch(cancelSubscriptionThunk(subscription, resourceIds, level)),
    };
}

function makeMapStateToProps() {
    const getServiceResourcesForSku = makeGetServiceResourcesForSku();

    return (state: RootState, props: OwnProps): StateProps => ({
        resources: getServiceResourcesForSku(state, props.service.sku) ?? [],
    });
}

export const ServiceBodyContainer = connect(makeMapStateToProps, mapDispatchToProps)(ServiceBody);
